import { Select, type SelectProps } from '@mantine/core';
import type { Except, Merge, SetRequired } from 'type-fest';
import { useCommonInputProps } from '../../../hooks/useCommonInputProps';
import type { Enum } from '../../../types/enum';
import type {
    Controlled_ValidatedInputProps,
    ValueOrNullableValue,
} from './types';

export type Controlled_EnumInputProps<
    E extends string,
    N extends boolean,
> = Merge<
    Except<
        SetRequired<SelectProps, 'readOnly'>,
        | 'comboboxProps'
        | 'allowDeselect'
        | 'searchable'
        | 'clearable'
        | 'data'
        | 'styles'
        | 'rightSectionWidth'
        | 'rightSection'
    >,
    Controlled_ValidatedInputProps<N, E> & {
        enumType: Enum<E>;
        enumFormatter?: (value: E) => string;
        typeFilter?: (value: E) => boolean;
    }
>;

export const Controlled_EnumInput = <E extends string, N extends boolean>({
    nullable,
    value,
    onChange,
    enumType,
    enumFormatter,
    typeFilter,
    label: _label,
    labelInfo,
    severity,
    helperText,
    endingIcon,
    placeholder: _placeholder,
    readOnly,
    w,
    nullableInReadOnly,
    ...props
}: Controlled_EnumInputProps<E, N>) => {
    const { rightSectionWidth, rightSection, inputStyles, label, placeholder } =
        useCommonInputProps({
            endingIcon,
            severity,
            helperText,
            nullable,
            onChange,
            value,
            label: _label,
            labelInfo,
            placeholder: _placeholder,
            readOnly,
            nullableInReadOnly,
            clearable: false,
            defaultValue: value,
        });

    const handleChange =
        onChange ?
            (value: string | null) =>
                onChange(value as ValueOrNullableValue<N, E>)
        :   undefined;

    const data = (() => {
        let result = Object.keys(enumType) as Array<E>;

        if (typeFilter) {
            result = result.filter(typeFilter);
        }

        if (!enumFormatter) {
            return result;
        }

        return result.map((key) => ({
            value: key,
            label: enumFormatter(key),
        }));
    })();

    return (
        <Select
            {...props}
            readOnly={readOnly}
            value={value}
            clearable={!readOnly && nullable}
            label={label}
            placeholder={placeholder}
            rightSectionWidth={rightSectionWidth}
            rightSection={rightSection}
            styles={{ input: inputStyles }}
            onChange={handleChange}
            data={data}
            w={w ?? '100%'}
            searchable={false}
            allowDeselect={false}
            comboboxProps={{ zIndex: 9000 }}
        />
    );
};
